import React from 'react';
import { isEmpty, findIndex, includes } from 'lodash';
import classnames from 'classnames';
import { Button } from 'new/elements/Button';
import { AutoSizer, List } from 'react-virtualized';
import { ReactComponent as TableIcon } from 'new/components/icons/fi-rr-table.svg';
import { CircularProgress } from '@mui/material';
import { RemoveRedEye as EyeIcon } from '@mui/icons-material';
import DebouncedText from './editors/mui/DebouncedText';

import './SearchableList.scss';

class SearchableList extends React.Component {
  constructor(props) {
    super(props);

    this.state = this._getStateFromProps(props, {
      customItem: null,
      searchText: null,
      focusedIndex: -1,
    });

    this._rowRenderer = this._rowRenderer.bind(this);
    this._scrollToRow = this._scrollToRow.bind(this);
    this._getIndexFromEvent = this._getIndexFromEvent.bind(this);
    this._handleSearchInputChanged = this._handleSearchInputChanged.bind(this);
    this._handleItemMouseEnter = this._handleItemMouseEnter.bind(this);
    this._handleSelectClicked = this._handleSelectClicked.bind(this);
    this._handlePreviewClicked = this._handlePreviewClicked.bind(this);
    this._handleCreateItem = this._handleCreateItem.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { focusedIndex, selectedIndex, customItem } = this.state;

    if (
      this._list &&
      (prevState.focusedIndex !== focusedIndex || prevState.selectedIndex !== selectedIndex)
    ) {
      this._list.forceUpdateGrid();
    }

    if (prevState.selectedIndex !== selectedIndex) {
      const { data, onChange } = this.props;

      if (onChange) {
        onChange(
          selectedIndex === 'custom' ? customItem : selectedIndex > -1 ? data[selectedIndex] : null,
        );
      }

      if (selectedIndex !== -1) {
        this.setState({ searchText: null });
      }
    }

    if (prevProps.selectedItem !== this.props.selectedItem || prevProps.data !== this.props.data) {
      //TODO:R16: need better data change comparision. also maybe don't need data in state?
      this.setState(this._getStateFromProps(this.props));
    }
  }

  render() {
    const { data, isLoading, hideCreateBtn } = this.props;
    if (isLoading) {
      return (
        <div className="searchable-list is-empty is-loading">
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="primary" size={20} />
          </div>
        </div>
      );
    }

    if (isEmpty(data)) {
      return <div className="searchable-list is-empty">No entries found.</div>;
    }

    const { searchText, selectedIndex, customItem } = this.state;
    const rowCount = data.length + (customItem ? 1 : 0);
    const rowHeight = 40;
    const numberOfLinesToRender = Math.min(rowCount, 10) * rowHeight - 1;

    return (
      <div className="searchable-list">
        <div className="search-input-container">
          <div className="search-icon-view">
            <TableIcon />
          </div>

          <DebouncedText
            type="text"
            value={
              searchText
                ? searchText
                : selectedIndex === 'custom'
                  ? customItem
                  : selectedIndex > -1
                    ? data[selectedIndex]
                    : ''
            }
            debounceTimeMs={200}
            rootStyle={{ height: 34 }}
            onKeyDown={this._handleKeyDown}
            onChange={this._handleSearchInputChanged}
          />

          {searchText && !includes(data, searchText) && !hideCreateBtn && (
            <Button
              buttonTheme="primary"
              rootStyle={{
                height: 28,
                position: 'absolute',
                top: 4,
                right: 4,
                marginRight: 0,
                lineHeight: '28px',
              }}
              onClick={this._handleCreateItem}
            >
              <span
                style={{
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--font-size-base-sm)',
                  color: 'white',
                }}
              >
                + CREATE
              </span>
            </Button>
          )}
        </div>

        <div className="list-container" style={{ height: numberOfLinesToRender }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                className="list"
                width={width}
                height={height}
                rowHeight={rowHeight}
                rowCount={rowCount}
                rowRenderer={this._rowRenderer}
                ref={(element) => {
                  this._list = element;
                }}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }

  _rowRenderer(row) {
    const { data, isPreviewEnabled } = this.props;
    const { focusedIndex, selectedIndex, customItem } = this.state;
    const { key, index: rowIndex, style } = row;
    const index = customItem && rowIndex === 0 ? 'custom' : customItem ? rowIndex - 1 : rowIndex;
    const isCustomItem = index === 'custom';

    return (
      <div
        key={key}
        data-index={index}
        className={classnames('list-item', {
          'is-custom-item': isCustomItem,
          focused: index === focusedIndex,
          selected: index === selectedIndex,
        })}
        style={style}
        onMouseEnter={this._handleItemMouseEnter}
        onClick={this._handleSelectClicked}
      >
        <span className="row-text">{isCustomItem ? customItem : data[index]}</span>

        <div className="buttons-container">
          {isPreviewEnabled && focusedIndex === index ? (
            <Button
              className="base-button-row"
              color="info"
              variant="outlined"
              data-index={index}
              onClick={this._handlePreviewClicked}
            >
              <EyeIcon fontSize="small" style={{ width: 12, height: 12 }} />
              Preview
            </Button>
          ) : null}

          <Button
            color="info"
            variant="outlined"
            data-index={index}
            className="base-button-row"
            onClick={this._handleSelectClicked}
          >
            {selectedIndex === index ? 'Selected' : 'Select'}
          </Button>
        </div>
      </div>
    );
  }

  _scrollToRow(index) {
    if (index > -1) {
      this._list.scrollToRow(index);
    }
  }

  _getStateFromProps(props, state = {}) {
    const { selectedItem, data } = props;
    const newState = { ...state };

    if (data) {
      let customItem = null;

      if (selectedItem && !includes(data, selectedItem)) {
        customItem = selectedItem;
        newState.customItem = customItem;
      }

      newState.selectedIndex = customItem ? 'custom' : data.indexOf(selectedItem);
    }

    return newState;
  }

  _getIndexFromEvent(event) {
    const index = event.currentTarget.dataset.index;
    const intIndex = parseInt(index, 10);

    return isNaN(intIndex) ? index : intIndex;
  }

  _handleSearchInputChanged(searchText) {
    const { data } = this.props;
    const { customItem } = this.state;
    const newState = { searchText };
    const lowerCaseSearchText = searchText.toLowerCase().trim();

    if (!lowerCaseSearchText) {
      newState.selectedIndex = -1;
    } else {
      const matchIndex = findIndex(data, (element) =>
        String(element).toLowerCase().includes(lowerCaseSearchText),
      );

      if (matchIndex > -1) {
        newState.focusedIndex = matchIndex;
        this._scrollToRow(matchIndex);
      } else if (customItem && customItem.toLowerCase().includes(lowerCaseSearchText)) {
        newState.focusedIndex = 'custom';
        this._scrollToRow(0);
      }
    }

    this.setState(newState);
  }

  _handleItemMouseEnter(event) {
    const index = this._getIndexFromEvent(event);

    this.setState({ focusedIndex: index });
  }

  _handleSelectClicked(event) {
    const { selectedIndex } = this.state;
    const index = this._getIndexFromEvent(event);

    this.setState({ selectedIndex: index === selectedIndex ? -1 : index });
  }

  _handlePreviewClicked(event) {
    event.stopPropagation();

    const { data, onPreviewClicked } = this.props;
    const { customItem } = this.state;
    const index = this._getIndexFromEvent(event);

    if (onPreviewClicked) {
      onPreviewClicked(index === 'custom' ? customItem : data[index]);
    }
  }

  _handleCreateItem() {
    const { onCreate } = this.props;
    const { searchText } = this.state;

    this.setState({ customItem: searchText }, () => {
      onCreate(searchText);
    });
  }

  _handleKeyDown(event) {
    const { data } = this.props;
    const { customItem, focusedIndex } = this.state;

    // for simplicity, disable keyboard navigation if there's a custom item
    if (customItem) {
      return;
    }

    let newFocusedIndex;

    switch (event.keyCode) {
      case 38: // Up
        newFocusedIndex = Math.max(-1, focusedIndex - 1);
        this.setState({ focusedIndex: newFocusedIndex });
        this._scrollToRow(newFocusedIndex);
        break;

      case 40: // Down
        newFocusedIndex = Math.min(data.length - 1, focusedIndex + 1);
        this.setState({ focusedIndex: newFocusedIndex });
        this._scrollToRow(newFocusedIndex);
        break;

      case 13: // Enter
        this.setState({ selectedIndex: focusedIndex });
        break;

      default:
    }
  }
}

export default SearchableList;
